import { useState } from "preact/hooks";

import { api } from "@/api/client";
import type { AnalystRequest, AnalystResponse, AnalystTurn } from "@/api/types";
import { ExcerptRefs } from "@/components/ExcerptRefs";
import { PanelHelp } from "@/components/PanelHelp";
import { t, tOr, type TranslationKey } from "@/i18n";
import { formatDateTime, formatInt, formatUsdFine, tenantLabel } from "@/lib/format";
import { renderAnalysisMarkdown } from "@/lib/renderMarkdown";
import { errorDetail } from "@/lib/runtimeChange";
import { analystCheck, analystWindow, includeTestSessions, selectedTenant } from "@/state/filters";

// «Gespräche › Analyse-Chat»: the analysis model as a sparring partner over the
// chat data of the window, partner and test setting the header shows, or over one
// Fragen-Check when opened from it. The backend hands it the data pack; this
// view holds the conversation, which is stored nowhere.

const STARTERS: readonly TranslationKey[] = [
  "analyst.starter.topics",
  "analyst.starter.sources",
  "analyst.starter.negative",
  "analyst.starter.cost",
];

// Opened from a Fragen-Check: questions about that check.
const CHECK_STARTERS: readonly TranslationKey[] = [
  "analyst.starter.check.noPage",
  "analyst.starter.check.spread",
  "analyst.starter.check.weak",
];

// The chat so far that goes along with a question; older messages drop out.
const HISTORY_MESSAGES = 12;

export interface AnalystMessage {
  role: "user" | "assistant";
  text: string;
  meta?: AnalystResponse;
}

/** The chat so far as the backend takes it: the newest messages, text only. */
export function analystHistory(messages: readonly AnalystMessage[]): AnalystTurn[] {
  return messages.slice(-HISTORY_MESSAGES).map(({ role, text }) => ({ role, text }));
}

/** A failed answer in words: the kind of failure, else the HTTP error. */
function analystErrorMessage(err: unknown): string {
  const detail = errorDetail(err);
  if (detail.error === "analyst_failed") {
    const kind = String(detail.kind ?? "");
    return t("analyst.error", { kind: tOr(`analyst.kind.${kind}`, tOr(`ops.probe.${kind}`, kind)) });
  }
  return err instanceof Error ? err.message : String(err);
}

export function AnalystView() {
  const [messages, setMessages] = useState<AnalystMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scope = analystCheck.value;

  const ask = async (question: string) => {
    const text = question.trim();
    if (!text || busy) return;
    const before = messages;
    const check = analystCheck.peek();
    setBusy(true);
    setError("");
    setDraft("");
    setMessages([...before, { role: "user", text }]);
    try {
      // A check is its own scope: the header shows no partner filter for it,
      // so none is sent (the backend still refuses an unknown one).
      const body: AnalystRequest = {
        question: text,
        history: analystHistory(before),
        window: analystWindow.value,
        include_test: includeTestSessions.value,
        tenant: check ? "" : selectedTenant.value,
        ...(check ? { check_id: check.id } : {}),
      };
      const meta = await api.analyst(body);
      setMessages([...before, { role: "user", text }, { role: "assistant", text: meta.answer, meta }]);
    } catch (err) {
      // The question comes back for another try.
      setMessages(before);
      setDraft(text);
      setError(analystErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // The chat so far was about the check: the chat data start a new one.
  const backToChatData = () => {
    analystCheck.value = null;
    setMessages([]);
    setError("");
  };

  return (
    <div style="display: grid; gap: 24px;">
      <section class="card">
        <h2>{t("analyst.title")}</h2>
        <PanelHelp k="analyst.overview" />
        {scope && (
          <div class="analyst-scope">
            <span>
              {t("analyst.check.scope", {
                name: scope.name || t("qc.unnamed"),
                date: formatDateTime(scope.createdAt),
                n: formatInt(scope.questions),
              })}
            </span>
            <button type="button" class="link-mono" disabled={busy} onClick={backToChatData}>
              {t("analyst.check.back")}
            </button>
          </div>
        )}
        {messages.length === 0 && (
          <div class="toolbar analyst-starters">
            {(scope ? CHECK_STARTERS : STARTERS).map((key) => (
              <button
                key={key}
                type="button"
                class="window-pill"
                disabled={busy}
                onClick={() => void ask(t(key))}
              >
                {t(key)}
              </button>
            ))}
          </div>
        )}
        <div class="analyst-log" role="log">
          {messages.map((message, i) =>
            message.role === "user" ? (
              <div key={i} class="analyst-message analyst-user">
                {message.text}
              </div>
            ) : (
              <Answer key={i} message={message} />
            ),
          )}
          {busy && (
            <div class="kpi-sub" role="status">
              {t("analyst.busy")}
            </div>
          )}
        </div>
        {error && (
          <div class="error-banner" role="alert">
            {error}
          </div>
        )}
        <form
          class="analyst-composer"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(draft);
          }}
        >
          <textarea
            aria-label={t("analyst.question")}
            placeholder={t(scope ? "analyst.placeholderCheck" : "analyst.placeholder")}
            rows={3}
            maxLength={2000}
            value={draft}
            onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
            onKeyDown={(e) => {
              // Enter asks; Shift+Enter starts a new line.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void ask(draft);
              }
            }}
          />
          <div class="toolbar">
            <button type="submit" class="window-pill active" disabled={busy || !draft.trim()}>
              {t("analyst.send")}
            </button>
            <button
              type="button"
              class="window-pill"
              disabled={busy || messages.length === 0}
              onClick={() => {
                setMessages([]);
                setError("");
              }}
            >
              {t("analyst.new")}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Answer({ message }: { message: AnalystMessage }) {
  const meta = message.meta;
  return (
    <div class="analyst-message analyst-answer">
      {/* Sanitised by renderAnalysisMarkdown (DOMPurify), like the inspector. */}
      <div
        class="rendered-answer"
        dangerouslySetInnerHTML={{ __html: renderAnalysisMarkdown(message.text) }}
      />
      {meta && (
        <div class="kpi-sub">
          {meta.check_id
            ? t("analyst.basisCheck", {
                excerpts: formatInt(meta.excerpts),
                cost: formatUsdFine(meta.cost_usd),
              })
            : t("analyst.basis", {
                turns: formatInt(meta.turns_in_window),
                excerpts: formatInt(meta.excerpts),
                window: meta.window_label,
                tenant: meta.tenant ? tenantLabel(meta.tenant) : t("analyst.allPartners"),
                cost: formatUsdFine(meta.cost_usd),
              })}
        </div>
      )}
      {meta && <ExcerptRefs refs={meta.excerpt_refs} />}
    </div>
  );
}
